// Customer/company credit ledger. Credit = money explicitly moved "up" to the
// client's profile (overpayments, cancellations, unreturned deposits), tracked
// as signed ledger entries — NOT a payment's unallocated_amount. Balance = SUM.
import supabase from "../supabase-client";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// The internal "Crédito de cliente" account: paying a reservation from credit
// uses it so Caja doesn't count it as a new bank deposit (the cash already
// entered once via the original payment).
export async function getCreditAccountId() {
  const { data } = await supabase
    .from("transaction_accounts")
    .select("id")
    .eq("type", "credit")
    .order("id")
    .limit(1)
    .maybeSingle();
  return data?.id ?? null;
}

export async function getCreditBalance(entityType, entityId) {
  if (!entityId) return 0;
  const { data } = await supabase
    .from("client_credit_ledger")
    .select("amount")
    .eq("entity_type", entityType)
    .eq("entity_id", entityId);
  return round2((data || []).reduce((s, r) => s + (r.amount || 0), 0));
}

export async function getCreditLedger(entityType, entityId) {
  if (!entityId) return [];
  const { data } = await supabase
    .from("client_credit_ledger")
    .select("*")
    .eq("entity_type", entityType)
    .eq("entity_id", entityId)
    .order("created_at", { ascending: false });
  return data || [];
}

// Move a reservation's overpayment up to the client's credit. Removes the
// amount from the reservation's unallocated payment(s) and posts a +entry.
export async function moveOverpaymentToCredit({
  entityType,
  entityId,
  reservationId,
  amount,
  createdBy,
}) {
  const amt = round2(amount);
  if (!entityId || !reservationId || amt <= 0) {
    return { success: false, error: "Datos incompletos" };
  }
  const { data: res } = await supabase
    .from("reservations")
    .select("total_price, paid")
    .eq("id", reservationId)
    .maybeSingle();
  if (!res) return { success: false, error: "Reservación no encontrada" };
  const excess = round2((res.paid || 0) - (res.total_price || 0));
  if (amt > excess + 0.01) {
    return { success: false, error: `No se puede mover más que el excedente (${excess.toFixed(2)})` };
  }

  // Draw the amount OUT of the reservation's unallocated payments (newest first).
  const { data: txns } = await supabase
    .from("transactions")
    .select("id, unallocated_amount")
    .eq("reservation", reservationId)
    .eq("type", "income")
    .eq("isDeposit", false)
    .gt("unallocated_amount", 0)
    .order("created_at", { ascending: false });
  // Plan the draws before writing anything, so an insufficient balance can't
  // leave payments debited with no ledger entry to show for it.
  let remaining = amt;
  const draws = []; // { id, prev, take }
  for (const t of txns || []) {
    if (remaining <= 0.005) break;
    const take = round2(Math.min(remaining, t.unallocated_amount || 0));
    if (take <= 0.005) continue;
    draws.push({ id: t.id, prev: t.unallocated_amount || 0, take });
    remaining = round2(remaining - take);
  }
  if (remaining > 0.01) {
    return { success: false, error: "No hay saldo sin aplicar suficiente en los pagos de la reservación." };
  }

  const restore = (rows) =>
    Promise.all(
      rows.map((d) =>
        supabase.from("transactions").update({ unallocated_amount: d.prev }).eq("id", d.id)
      )
    );
  const done = [];
  for (const d of draws) {
    const { error } = await supabase
      .from("transactions")
      .update({ unallocated_amount: round2(d.prev - d.take) })
      .eq("id", d.id);
    if (error) {
      await restore(done);
      return { success: false, error: error.message };
    }
    done.push(d);
  }

  // One entry PER source payment: applyAllocations subtracts the credit moved
  // out of each payment (by source_transaction_id) when it re-derives that
  // payment's unallocated, so every draw has to be attributable.
  const { error: ledErr } = await supabase.from("client_credit_ledger").insert(
    draws.map((d) => ({
      entity_type: entityType,
      entity_id: entityId,
      amount: d.take,
      reason: "overpayment",
      source_reservation_id: reservationId,
      source_transaction_id: d.id,
      created_by: createdBy || null,
    }))
  );
  if (ledErr) {
    await restore(done);
    return { success: false, error: ledErr.message };
  }

  await supabase.rpc("recompute_reservation_payments", { p_reservation_id: reservationId });
  return { success: true };
}

// Bring credit down to a reservation. By default it posts an internal-account
// payment that sits as unapplied (to be conciliated) and a -entry on the ledger.
// When isDeposit is true it instead records a deposit transaction (collateral,
// excluded from `paid`, never invoiced) — i.e. paying the deposit from credit.
export async function bringCreditToReservation({
  entityType,
  entityId,
  reservationId,
  amount,
  createdBy,
  isDeposit = false,
}) {
  const amt = round2(amount);
  if (!entityId || !reservationId || amt <= 0) {
    return { success: false, error: "Datos incompletos" };
  }
  const balance = await getCreditBalance(entityType, entityId);
  if (amt > balance + 0.01) {
    return { success: false, error: `Crédito insuficiente (disponible ${balance.toFixed(2)})` };
  }
  const accountId = await getCreditAccountId();
  if (!accountId) return { success: false, error: "Falta la cuenta interna 'Crédito de cliente'." };

  const { data: txn, error: txErr } = await supabase
    .from("transactions")
    .insert({
      type: "income",
      amount: amt,
      account: accountId,
      reservation: reservationId,
      billing_entity_type: entityType,
      billing_entity_id: entityId,
      customer: entityType === "customer" ? entityId : null,
      isDeposit,
      // Deposits are collateral: no residual to conciliate, never invoiced.
      unallocated_amount: isDeposit ? 0 : amt,
      invoiced: isDeposit ? true : false,
      payment_date: new Date().toLocaleDateString("en-CA"),
    })
    .select("id")
    .single();
  if (txErr) return { success: false, error: txErr.message };

  const { error: ledErr } = await supabase.from("client_credit_ledger").insert({
    entity_type: entityType,
    entity_id: entityId,
    amount: -amt,
    reason: "applied",
    target_reservation_id: reservationId,
    transaction_id: txn.id,
    account_id: accountId,
    created_by: createdBy || null,
  });
  if (ledErr) return { success: false, error: ledErr.message };

  await supabase.rpc("recompute_reservation_payments", { p_reservation_id: reservationId });
  return { success: true, transactionId: txn.id };
}

// Refund credit as a real cash payout from a bank account.
export async function refundCredit({ entityType, entityId, amount, accountId, createdBy }) {
  const amt = round2(amount);
  if (!entityId || amt <= 0 || !accountId) return { success: false, error: "Datos incompletos" };
  const balance = await getCreditBalance(entityType, entityId);
  if (amt > balance + 0.01) {
    return { success: false, error: `Crédito insuficiente (disponible ${balance.toFixed(2)})` };
  }

  // A bank payout is a SPEI that still has to be sent, so it stays pending in
  // the transfers queue; cash/card settle on the spot. `transferred` defaults
  // to true in the DB, so it must be set explicitly or the refund never shows
  // up as pending. Mirrors the reservation refund flow.
  const { data: acc } = await supabase
    .from("transaction_accounts")
    .select("type")
    .eq("id", parseInt(accountId))
    .maybeSingle();
  const transferred = acc?.type !== "bank";
  const now = new Date().toISOString();

  const { data: txn, error: txErr } = await supabase
    .from("transactions")
    .insert({
      type: "refund",
      amount: amt,
      account: parseInt(accountId),
      billing_entity_type: entityType,
      billing_entity_id: entityId,
      customer: entityType === "customer" ? entityId : null,
      isDeposit: false,
      unallocated_amount: 0,
      payment_date: now.substring(0, 10),
      transferred,
      transferred_at: transferred ? now : null,
      created_by: createdBy || null,
    })
    .select("id")
    .single();
  if (txErr) return { success: false, error: txErr.message };

  const { error: ledErr } = await supabase.from("client_credit_ledger").insert({
    entity_type: entityType,
    entity_id: entityId,
    amount: -amt,
    reason: "refund",
    transaction_id: txn.id,
    account_id: parseInt(accountId),
    created_by: createdBy || null,
  });
  if (ledErr) return { success: false, error: ledErr.message };
  return { success: true };
}
